import {
  Backdrop,
  Box,
  IconButton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { usePreventScroll } from '@react-aria/overlays';
import { m } from 'motion/react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MAX_BENEFITS_BEFORE_EXTRA_SPACE } from 'constants/loyalty.constants';
import { brandConfig } from 'constants/saudi-rewards.constants';
import { NofomoLoyaltyStoreState } from 'services/stores/loyalty/zustand';
import { LoyaltyTierFragment } from 'types/api/account.types';

import Icon from 'components/@icons/Icon';
import LoyaltyCardPortrait from 'components/@loyalty/LoyaltyCardPortrait';
import LoyaltyCardPortraitBenefits from 'components/@loyalty/LoyaltyCardPortraitBenefits';
import SaudiRewardsCardPortrait from 'components/@saudi-rewards/SaudiRewardsCardPortrait';

import LoyaltyContent from './LoyaltyProgressContent';
import SaudiRewardsGradientIcon from './SaudiRewardsGradientLogo';
import { loyaltyVariants, saudiRewardsVariants } from './utils';

interface Props {
  isSaudiRewardsLinked: boolean;
  totalPointsGained: number;
  tiersToAnimate: Array<LoyaltyTierFragment>;
  loyaltyTiers: Array<LoyaltyTierFragment>;
  previousState: NofomoLoyaltyStoreState['previous'];
}

const LoyaltyCelebration = ({
  isSaudiRewardsLinked,
  totalPointsGained,
  loyaltyTiers,
  tiersToAnimate,
  previousState,
}: Props) => {
  // Animation state
  const [showSaudiRewards, setShowSaudiRewards] = useState(false);
  const [showLoyalty, setShowLoyalty] = useState(false);
  const [showLoyaltyTierUpgrade, setShowLoyaltyTierUpgrade] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);

  const intl = useIntl();
  usePreventScroll({ isDisabled: !showLoyaltyTierUpgrade });
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const [isCompleted, setIsCompleted] = useState(false);

  // Tier progression logic
  const [currentTierIndex, setCurrentTierIndex] = useState(0);
  const currentTier = tiersToAnimate[currentTierIndex];
  const hasMoreTiers = currentTierIndex < tiersToAnimate.length - 1;

  const previousTierIndex = loyaltyTiers.findIndex(
    (tier) => tier.id === currentTier.id,
  );
  const nextTier = loyaltyTiers[previousTierIndex + 1];

  // Calculate points for display
  const currentPoints =
    currentTier.id === previousState.tier ? previousState.points : 0;

  const nextPoints =
    currentTier.id === previousState.tier
      ? previousState.points + totalPointsGained
      : hasMoreTiers
        ? nextTier?.minPoints
        : previousState.points + totalPointsGained;

  // Determine if progress bar should be shown
  const shouldShowProgressBar =
    tiersToAnimate.length > 1 || currentTier.id !== 'Royal';

  const showTierUpgrade = tiersToAnimate.length > 1;

  const handleNextTier = () => {
    if (hasMoreTiers) {
      setCurrentTierIndex((prev) => prev + 1);
    } else {
      if (showTierUpgrade) {
        setShowLoyaltyTierUpgrade(true);
        setShowBenefits(true);
      } else {
        completeAnimation();
      }
    }
  };

  // Handle animation completion
  const completeAnimation = () => {
    setIsCompleted(true);
  };

  // Animation state helpers
  const getSaudiRewardsAnimationState = () => {
    if (isCompleted && showTierUpgrade) return 'completedAfterUpgradeClose';
    if (isCompleted) return 'completed';
    if (showSaudiRewards && showLoyalty) return 'stacked';
    if (showSaudiRewards) return 'initial';
    return 'hidden';
  };

  const getLoyaltyAnimationState = () => {
    if (isCompleted && showTierUpgrade) return 'completedAfterUpgradeClose';
    if (isCompleted) return 'completed';
    if (showLoyalty && showLoyaltyTierUpgrade && isMobile)
      return 'expandedMobile';
    if (showLoyalty && showLoyaltyTierUpgrade) return 'expanded';
    if (showLoyalty && shouldShowProgressBar) return 'initial';
    if (showLoyalty) return 'initialWithoutProgress';
    return 'hidden';
  };

  // Small timeout is usefull to load the images before the animation starts
  useEffect(() => {
    // Saudi Rewards animation (if applicable)
    if (isSaudiRewardsLinked) {
      setTimeout(() => {
        setShowSaudiRewards(true);
      }, 500);
    }

    // Loyalty animation (with appropriate timing)
    setTimeout(
      () => {
        setShowLoyalty(true);
      },
      isSaudiRewardsLinked ? 2200 : 500,
    );
  }, [isSaudiRewardsLinked]);

  const activeBenefits = currentTier.benefits.filter(
    (benefit) => !!benefit.value,
  );

  const handleClosePortraitCard = () => {
    setShowLoyaltyTierUpgrade(false);
    // Small delay here to avoid content shift for the user. (opacity was not possible because mix-blend-mode does not support it)
    setTimeout(() => setShowBenefits(false), 350);

    // Give the close animation time to finish, then complete the full animation.
    setTimeout(() => completeAnimation(), 700);
  };

  return (
    <>
      <Backdrop
        sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
        open={showLoyaltyTierUpgrade}
      />
      {!isMobile && showLoyaltyTierUpgrade && (
        <IconButton
          aria-label={intl.formatMessage({ id: 'navigation.close' })}
          onClick={handleClosePortraitCard}
          sx={(theme) => ({
            position: 'absolute',
            right: { xs: 16, md: 24 },
            top: { xs: 16, md: 24 },
            zIndex: theme.zIndex.drawer + 1,
          })}
        >
          <Icon name="Close" />
        </IconButton>
      )}

      <Box
        sx={(theme) => ({
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          zIndex: theme.zIndex.drawer + 1,
          maxWidth: { sm: 376 },
        })}
      >
        <Box
          component={m.div}
          animate={getSaudiRewardsAnimationState()}
          variants={saudiRewardsVariants}
          initial="hidden"
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
          }}
        >
          <Backdrop
            sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
            open={showLoyaltyTierUpgrade}
          />
          <SaudiRewardsCardPortrait>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                position: 'absolute',
                left: 0,
                bottom: 24,
                px: 3,
              }}
            >
              <SaudiRewardsGradientIcon
                start={brandConfig.gradient.start}
                end={brandConfig.gradient.end}
                style={{ minWidth: 56 }}
              />
              <Stack>
                <Typography variant="extraSmall" fontWeight="bold" mb="2px">
                  <FormattedMessage id="account.saudi.rewards.title" />
                </Typography>
                <Typography variant="extraSmall">
                  <FormattedMessage id="tickets.checkout.confirmation.saudi.rewards.earned" />
                </Typography>
              </Stack>
            </Box>
          </SaudiRewardsCardPortrait>
        </Box>
        <Box
          component={m.div}
          animate={getLoyaltyAnimationState()}
          variants={loyaltyVariants}
          initial="hidden"
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: 1205,
            // Ensure it can be scrolled on low height mobile devices.
            maxHeight: showLoyaltyTierUpgrade ? '100svh' : undefined,
            overflowY: showLoyaltyTierUpgrade ? 'auto' : undefined,
          }}
        >
          <LoyaltyCardPortrait
            tier={currentTier.id}
            onClose={isMobile ? handleClosePortraitCard : undefined}
            isProgressAnimation={!showLoyaltyTierUpgrade && !showBenefits}
            logoExtraSpace={
              activeBenefits.length > MAX_BENEFITS_BEFORE_EXTRA_SPACE
            }
          >
            <LoyaltyCardPortraitBenefits
              tier={currentTier.id}
              benefits={activeBenefits}
              sx={{
                display:
                  showLoyaltyTierUpgrade || showBenefits ? 'flex' : 'none',
              }}
            />

            {showLoyalty && (
              <LoyaltyContent
                isInitial={currentTierIndex === 0}
                currentTier={currentTier}
                nextTier={nextTier}
                currentPoints={currentPoints}
                nextPoints={nextPoints}
                totalPointsGained={totalPointsGained}
                onTierComplete={handleNextTier}
                showProgress={shouldShowProgressBar}
                sx={{
                  display:
                    !showLoyaltyTierUpgrade && showLoyalty && !showBenefits
                      ? 'flex'
                      : 'none',
                }}
              />
            )}
          </LoyaltyCardPortrait>
        </Box>
      </Box>
    </>
  );
};

export default LoyaltyCelebration;
